import React, { useState, useEffect } from "react";
import { Edit, Trash } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../services/apiService";

const Album = () => {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAlbumId, setSelectedAlbumId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAlbums, setTotalAlbums] = useState(0);
  const limit = 20;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchAlbums();
    } else {
      navigate("/");
    }
  }, [navigate, currentPage]);

  const fetchAlbums = async () => {
    try {
      const response = await apiService.postData("admin/album-list", {
        limit: limit,
        offset: (currentPage - 1) * limit,
      });

      if (!response.error && response.data?.rows) {
        setAlbums(response.data.rows);
        setTotalAlbums(response.data.count || response.data.Count);
      }
    } catch (error) {
      console.error("Error fetching albums:", error);
    }
  };

  const handleEdit = (albumId) => {
    navigate(`/edit-album/${albumId}`);
  };

  const confirmDeleteAlbum = (albumId) => {
    setSelectedAlbumId(albumId);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (selectedAlbumId) {
      try {
        await apiService.deleteData(`admin/delete-album/${selectedAlbumId}`);
        fetchAlbums();
        setIsModalOpen(false);
      } catch (error) {
        console.error("Error deleting album:", error);
      }
    }
  };

  // Pagination logic
  const totalPages = totalAlbums > 0 ? Math.ceil(totalAlbums / limit) : 1;
  const isNextDisabled =
    albums.length < limit || currentPage * limit >= totalAlbums;

  return (
    <div className="col-md-12">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4>Albums</h4>
          <button
            onClick={() => navigate("/new-album")}
            className="btn btn-danger"
          >
            New Album <i className="fas fa-plus"></i>
          </button>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Name</th>
                  <th>Cover</th>
                  <th>Images</th>
                  <th>RFID</th>
                  <th style={{ width: "150px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {albums.length > 0 ? (
                  albums.map((album) => (
                    <tr key={album.album_id}>
                      <td>{album.category || "-"}</td>
                      <td>{album.description || "-"}</td>
                      <td className="font-weight-bold">{album.name || "-"}</td>
                      <td>
                        {album.cover ? (
                          <img
                            src={apiService.getFullImageUrl(album.cover)}
                            alt="Cover"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "5px",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {album.image ? (
                          <img
                            src={apiService.getFullImageUrl(album.image)}
                            alt="Image"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "5px",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{album.rfid || "-"}</td>
                      <td>
                        <button
                          onClick={() => handleEdit(album.album_id)}
                          className="btn btn-primary btn-sm text-white me-2"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => confirmDeleteAlbum(album.album_id)}
                          className="btn btn-danger btn-sm text-white"
                        >
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : currentPage === 1 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No albums found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <nav className="d-flex justify-content-center align-items-center my-3">
        <button
          className={`btn btn-outline-primary me-2 ${
            currentPage === 1 ? "disabled" : ""
          }`}
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          «
        </button>
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index + 1}
            className={`btn mx-1 ${
              currentPage === index + 1 ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </button>
        ))}
        <button
          className={`btn btn-outline-primary ms-2 ${
            isNextDisabled ? "disabled" : ""
          }`}
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={isNextDisabled}
        >
          »
        </button>
      </nav>

      {/* Delete Confirmation Modal */}
      {isModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this album?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-danger" onClick={handleDelete}>
                  Yes
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Album;
